import { useState, FormEvent } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

type FormStatus = "idle" | "submitting" | "success" | "error";

const contactReasons = [
  "General Inquiry",
  "Collaboration",
  "Press & Media",
  "Community & Events",
  "Feedback",
];

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      return;
    }

    setStatus("submitting");
    const formData = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        formData.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch("https://readdy.ai/api/form/d6sdp43vrivh3q8klv5g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="bg-[#F4F3F0]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-44 pb-20 px-8 lg:px-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/74d1340644b4e10167dd20a45cb60f61.jpg"
            alt="Writing desk"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#F4F3F0]/90" />
        </div>
        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-4 block">Let's Connect</span>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#1A1A1A] leading-[1.15] mb-6">
            Say hello.
          </h1>
          <p className="text-[#555] text-lg leading-relaxed">
            Whether you have a question, want to collaborate, or just want to tell us what you're reading — we'd like to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-8 lg:px-24 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-16">

          {/* Info Column */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="font-serif text-3xl text-[#1A1A1A] mb-3">We read all messages.</h2>
              <p className="text-[#666] text-sm leading-relaxed">
                We aim to respond within 2 business days. For urgent inquiries, feel free to reach us directly by email.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#F4F3F0] rounded">
                  <i className="ri-mail-line text-[#3A5544]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#888] mb-1">Email</p>
                  <a href="mailto:hello@10pagesaday.com" className="text-[#1A1A1A] text-sm hover:text-[#3A5544] transition-colors cursor-pointer">hello@10pagesaday.com</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#F4F3F0] rounded">
                  <i className="ri-instagram-line text-[#3A5544]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#888] mb-1">Instagram</p>
                  <a href="https://instagram.com" target="_blank" rel="nofollow noopener noreferrer" className="text-[#1A1A1A] text-sm hover:text-[#3A5544] transition-colors cursor-pointer">@10pagesaday</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#F4F3F0] rounded">
                  <i className="ri-twitter-x-line text-[#3A5544]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#888] mb-1">Twitter / X</p>
                  <a href="https://twitter.com" target="_blank" rel="nofollow noopener noreferrer" className="text-[#1A1A1A] text-sm hover:text-[#3A5544] transition-colors cursor-pointer">@10pagesaday</a>
                </div>
              </div>
            </div>

            <div className="bg-[#F4F3F0] rounded-md p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#888] mb-3">Currently reading</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-16 rounded flex-shrink-0 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=close%20up%20of%20a%20book%20cover%20spine%20in%20warm%20neutral%20tones%20with%20elegant%20minimal%20typography%2C%20hardcover%20book%20aesthetic%2C%20soft%20lighting%2C%20literary%20photography&width=100&height=140&seq=cr-book-1&orientation=portrait"
                    alt="Current read"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-[#1A1A1A] text-sm font-medium">Atomic Habits</p>
                  <p className="text-[#888] text-xs">James Clear</p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="h-1.5 bg-[#3A5544] rounded-full" style={{ width: "68%" }} />
                    <div className="h-1.5 bg-[#E8E5E0] rounded-full flex-1" />
                  </div>
                  <p className="text-[#AAA] text-xs mt-1">68% complete</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {status === "success" ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 bg-[#F4F3F0] rounded-full">
                  <i className="ri-check-double-line text-3xl text-[#3A5544]" />
                </div>
                <h3 className="font-serif text-3xl text-[#1A1A1A] mb-4">Message received.</h3>
                <p className="text-[#666] text-sm leading-relaxed max-w-sm mx-auto mb-6">
                  Thank you for reaching out. We'll get back to you within 2 business days.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="text-[#3A5544] text-sm font-medium cursor-pointer hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-2">First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      placeholder="Your first name"
                      required
                      className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-2">Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      placeholder="Your last name"
                      required
                      className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    required
                    className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-2">Reason for Contact</label>
                  <select
                    name="reason"
                    required
                    className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded-md px-4 py-3 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#3A5544] transition-colors cursor-pointer"
                  >
                    <option value="">Select a reason...</option>
                    {contactReasons.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-[#888] mb-2">
                    Message <span className="text-[#CCC] normal-case tracking-normal font-normal">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    placeholder="Tell us what's on your mind..."
                    required
                    rows={5}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-[#F4F3F0] border border-[#E0DDD8] rounded-md px-4 py-3 text-sm text-[#1A1A1A] placeholder-[#CCC] focus:outline-none focus:border-[#3A5544] transition-colors resize-none"
                  />
                  {charCount > 500 && (
                    <p className="text-red-500 text-xs mt-1">Message must be under 500 characters.</p>
                  )}
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting" || charCount > 500}
                  className="w-full bg-[#3A5544] text-white py-3.5 rounded font-medium hover:bg-[#2D4238] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                >
                  {status === "submitting" ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Bottom Quote */}
      <section className="py-20 px-8 lg:px-24 bg-[#F4F3F0]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-serif text-2xl text-[#1A1A1A] leading-relaxed mb-4">
            "Not all readers are leaders, but all leaders are readers."
          </p>
          <p className="text-[#888] text-sm">— Harry S. Truman</p>
        </div>
      </section>

      <Footer />
    </main>
  );
}
